import { z } from 'zod';
import { competitionCodeSchema, competitionTierSchema, qualityScoreSchema } from './international-competition.js';
import { importBatchScopeSeasonSchema, publicResearchMatchDetailCoreSideSchema, publicResearchMatchDetailCoverageSchema, publicResearchMatchDetailGroupsSchema, publicResearchMatchDetailRosterDetailSchema, researchProviderSchema, worldcupResearchAcceptanceTierSchema, worldcupResearchSourceUsageSchema, } from './worldcup-research.js';
const nonEmptyStringSchema = z.string().trim().min(1);
const timestampSchema = z.string().trim().min(1);
const nonNegativeIntSchema = z.number().int().min(0);
export const researchReadModelSchemaVersion = 'national-team-research-read-models@1';
export const researchReadModelProjectionVersion = 'national-team-research-read-models-projection@1';
export const researchReadModelSchemaVersionSchema = z.literal(researchReadModelSchemaVersion);
export const researchReadModelProjectionVersionSchema = z.literal(researchReadModelProjectionVersion);
export const researchReadModelFreshnessSchema = z.object({
    status: z.enum(['fresh', 'stale', 'unknown']),
    checkedAt: timestampSchema,
    activeAcceptedBatchIds: z.array(nonEmptyStringSchema),
    staleReason: nonEmptyStringSchema.nullable(),
});
export const researchReadModelMetaSchema = z.object({
    schemaVersion: researchReadModelSchemaVersionSchema,
    projectionVersion: researchReadModelProjectionVersionSchema,
    dataVersion: nonEmptyStringSchema,
    contentHash: nonEmptyStringSchema,
    generatedAt: timestampSchema,
    acceptedBatchIds: z.array(nonEmptyStringSchema),
    sourceUsage: worldcupResearchSourceUsageSchema,
    freshness: researchReadModelFreshnessSchema,
});
export const readModelMetaCompatibilityWrapperSchema = z.object({
    readModelMeta: researchReadModelMetaSchema,
});
export const sanitizedResearchSourceReferenceSchema = z.object({
    provider: researchProviderSchema,
    endpointCategory: nonEmptyStringSchema,
    importBatchId: nonEmptyStringSchema,
    payloadId: nonEmptyStringSchema.optional(),
    payloadHash: nonEmptyStringSchema.optional(),
    caveats: z.array(nonEmptyStringSchema),
});
export const researchMatchScoreSchema = z.object({
    home: nonNegativeIntSchema.nullable(),
    away: nonNegativeIntSchema.nullable(),
});
export const matchIndexQualitySchema = z.object({
    dataQualityScore: qualityScoreSchema.nullable(),
    recommendedWeight: qualityScoreSchema.nullable(),
    acceptanceTier: worldcupResearchAcceptanceTierSchema,
    warnings: z.array(nonEmptyStringSchema),
});
export const matchIndexReadModelSchema = z.object({
    matchId: nonEmptyStringSchema,
    season: importBatchScopeSeasonSchema,
    competitionCode: competitionCodeSchema,
    competitionName: nonEmptyStringSchema,
    competitionTier: competitionTierSchema,
    stage: nonEmptyStringSchema,
    groupRound: nonEmptyStringSchema.nullable(),
    kickoffAt: timestampSchema.nullable(),
    homeTeam: nonEmptyStringSchema,
    awayTeam: nonEmptyStringSchema,
    score: researchMatchScoreSchema,
    quality: matchIndexQualitySchema,
    availableFields: z.array(nonEmptyStringSchema),
});
export const matchDetailHeaderSchema = z.object({
    matchId: nonEmptyStringSchema,
    importBatchId: nonEmptyStringSchema,
    season: importBatchScopeSeasonSchema,
    competitionCode: competitionCodeSchema,
    competitionName: nonEmptyStringSchema,
    competitionTier: competitionTierSchema,
    dataQualityScore: qualityScoreSchema.nullable(),
    recommendedWeight: qualityScoreSchema.nullable(),
    stage: nonEmptyStringSchema,
    groupRound: nonEmptyStringSchema.nullable(),
    kickoffAt: timestampSchema.nullable(),
    homeTeam: nonEmptyStringSchema,
    awayTeam: nonEmptyStringSchema,
    score: researchMatchScoreSchema,
});
export const matchDetailReadModelSchema = z.object({
    meta: researchReadModelMetaSchema,
    match: matchDetailHeaderSchema,
    canonicalCore: z.object({
        home: publicResearchMatchDetailCoreSideSchema.nullable(),
        away: publicResearchMatchDetailCoreSideSchema.nullable(),
    }),
    analysisEnrichment: publicResearchMatchDetailGroupsSchema,
    rosterDetail: publicResearchMatchDetailRosterDetailSchema.optional(),
    sourceReferences: z.array(sanitizedResearchSourceReferenceSchema),
    sourceNotes: z.array(nonEmptyStringSchema),
    coverage: publicResearchMatchDetailCoverageSchema,
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
});
export const teamContextReadModelSchema = z.object({
    meta: researchReadModelMetaSchema,
    team: nonEmptyStringSchema,
    scope: z.object({
        competitionCode: competitionCodeSchema.optional(),
        competitionName: nonEmptyStringSchema.optional(),
        seasons: z.array(importBatchScopeSeasonSchema),
        opponent: nonEmptyStringSchema.optional(),
    }),
    summary: z.object({
        matches: nonNegativeIntSchema,
        goalsFor: nonNegativeIntSchema,
        goalsAgainst: nonNegativeIntSchema,
        avgShotsTotal: z.number().nullable(),
        avgShotsOnTarget: z.number().nullable(),
        avgCorners: z.number().nullable(),
        avgExpectedGoals: z.number().nullable(),
    }),
    quality: z.object({
        acceptanceTiers: z.array(worldcupResearchAcceptanceTierSchema),
        warnings: z.array(nonEmptyStringSchema),
    }),
    matches: z.array(matchIndexReadModelSchema),
    sourceReferences: z.array(sanitizedResearchSourceReferenceSchema),
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
});
export const teamComparisonFactsReadModelSchema = z.object({
    meta: researchReadModelMetaSchema,
    teams: z.tuple([nonEmptyStringSchema, nonEmptyStringSchema]),
    scope: z.object({
        competitionCode: competitionCodeSchema.optional(),
        competitionName: nonEmptyStringSchema.optional(),
        seasons: z.array(importBatchScopeSeasonSchema),
    }),
    contexts: z.tuple([teamContextReadModelSchema, teamContextReadModelSchema]),
    headToHeadMatches: z.array(matchIndexReadModelSchema),
    sourceReferences: z.array(sanitizedResearchSourceReferenceSchema),
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
});
export const competitionCoverageReadModelSchema = z.object({
    meta: researchReadModelMetaSchema,
    competitionCode: competitionCodeSchema,
    competitionName: nonEmptyStringSchema,
    seasons: z.array(importBatchScopeSeasonSchema),
    acceptedBatchIds: z.array(nonEmptyStringSchema),
    fieldAvailability: z.record(nonEmptyStringSchema, z.array(nonEmptyStringSchema)),
    gaps: z.array(z.object({
        targetId: nonEmptyStringSchema.optional(),
        season: importBatchScopeSeasonSchema.optional(),
        reason: nonEmptyStringSchema,
        warnings: z.array(nonEmptyStringSchema),
    })),
    warnings: z.array(nonEmptyStringSchema),
});
export const researchDictionaryLabelSchema = z.object({
    en: nonEmptyStringSchema,
    zhCN: nonEmptyStringSchema,
});
export const researchDictionaryTeamSchema = z.object({
    canonical: nonEmptyStringSchema,
    aliases: z.array(nonEmptyStringSchema),
    labels: researchDictionaryLabelSchema,
});
export const researchDictionaryCompetitionSchema = z.object({
    code: competitionCodeSchema,
    canonicalName: nonEmptyStringSchema,
    labels: researchDictionaryLabelSchema,
});
export const researchDictionarySeasonSchema = z.object({
    competitionCode: competitionCodeSchema,
    seasons: z.array(importBatchScopeSeasonSchema),
});
export const researchDictionaryReadModelSchema = z.object({
    meta: researchReadModelMetaSchema,
    teams: z.array(researchDictionaryTeamSchema),
    competitions: z.array(researchDictionaryCompetitionSchema),
    seasons: z.array(researchDictionarySeasonSchema),
});
export const searchMatchesToolRequestSchema = z.object({
    competitionCode: competitionCodeSchema.optional(),
    competitionName: nonEmptyStringSchema.optional(),
    season: importBatchScopeSeasonSchema.optional(),
    team: nonEmptyStringSchema.optional(),
    opponent: nonEmptyStringSchema.optional(),
    limit: z.number().int().min(1).max(500).default(25),
    offset: z.number().int().min(0).default(0),
});
export const searchMatchesToolResponseSchema = z.object({
    meta: researchReadModelMetaSchema,
    appliedFilters: searchMatchesToolRequestSchema,
    pagination: z.object({
        limit: z.number().int().min(1).max(500),
        offset: z.number().int().min(0),
        total: nonNegativeIntSchema,
        hasMore: z.boolean(),
    }),
    matches: z.array(matchIndexReadModelSchema),
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
});
export const getMatchDetailToolRequestSchema = z.object({
    matchId: nonEmptyStringSchema,
});
export const getMatchDetailToolResponseSchema = matchDetailReadModelSchema;
export const getTeamContextToolRequestSchema = z.object({
    team: nonEmptyStringSchema,
    competitionCode: competitionCodeSchema.optional(),
    competitionName: nonEmptyStringSchema.optional(),
    season: importBatchScopeSeasonSchema.optional(),
    opponent: nonEmptyStringSchema.optional(),
});
export const getTeamContextToolResponseSchema = teamContextReadModelSchema;
export const compareTeamsToolRequestSchema = z.object({
    teams: z.tuple([nonEmptyStringSchema, nonEmptyStringSchema]),
    competitionCode: competitionCodeSchema.optional(),
    competitionName: nonEmptyStringSchema.optional(),
    season: importBatchScopeSeasonSchema.optional(),
});
export const compareTeamsToolResponseSchema = teamComparisonFactsReadModelSchema;
export const getCompetitionCoverageToolRequestSchema = z.object({
    competitionCode: competitionCodeSchema.optional(),
    competitionName: nonEmptyStringSchema.optional(),
    season: importBatchScopeSeasonSchema.optional(),
});
export const getCompetitionCoverageToolResponseSchema = competitionCoverageReadModelSchema;
export const getResearchDictionaryToolRequestSchema = z.object({
    locale: z.enum(['en', 'zh-CN', 'zhCN', 'zh-TW', 'ja', 'ko']).optional(),
});
export const getResearchDictionaryToolResponseSchema = researchDictionaryReadModelSchema;
export const readModelToolOperationSchema = z.enum([
    'search_matches',
    'get_match_detail',
    'get_team_context',
    'compare_teams',
    'get_competition_coverage',
    'get_research_dictionary',
    'get_opportunity_context',
]);
export const readModelToolResponseBoundarySchema = z.object({
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
});
export const getOpportunityContextToolRequestSchema = z.object({
    matchId: nonEmptyStringSchema,
});
export const getOpportunityContextToolResponseSchema = z.object({
    meta: researchReadModelMetaSchema,
    status: z.enum(['found', 'not_mapped']),
    matchId: nonEmptyStringSchema.optional(),
    match: matchIndexReadModelSchema.nullable(),
    teamContexts: z.array(teamContextReadModelSchema),
    warnings: z.array(nonEmptyStringSchema),
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
});
export const nationalTeamResearchReadModelSnapshotSchema = z.object({
    meta: researchReadModelMetaSchema,
    scope: z.object({
        researchView: z.enum(['major_tournament', 'competitive_context', 'exploratory_all']),
        minDataQualityScore: qualityScoreSchema,
        competitionCode: competitionCodeSchema.optional(),
        competitionName: nonEmptyStringSchema.optional(),
        explicitEmptyScopeReason: nonEmptyStringSchema.optional(),
    }),
    matches: z.array(matchIndexReadModelSchema),
    coverage: z.array(competitionCoverageReadModelSchema),
    dictionary: researchDictionaryReadModelSchema,
    source: z.object({
        providerFetchesPerformed: z.literal(false),
        rawProviderPayloadBodiesIncluded: z.literal(false),
        sensitiveValuesIncluded: z.literal(false),
    }),
});
